/**
 * Anomalies — heuristic alarms surfaced by the licensing engine.
 *
 * Layout:
 *   PageHeader → HeroPanel (active + severity buckets)
 *              → Filter chips (severity + unresolved-only)
 *              → Severity-railed anomaly cards (vertical list)
 *              → Drawer for ack/notes/dismiss
 *
 * Notes:
 *   - There is no /internal/anomalies/:id/ack endpoint yet, so notes
 *     stored in the drawer are local-only. The dismiss action calls
 *     deleteAnomaly (owner-only).
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  AlertTriangle, RefreshCw, ShieldCheck, Trash2,
  Clock, KeyRound, ShieldOff, Settings2,
} from 'lucide-react';
import { AdminApiClient,  type AnomalyRow } from '../api';
import { useSession } from '../session';
import { usePermissions } from '../lib/permissions';
import { failureFrom, userMessageFor, type Failure } from '../lib/api-state';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';
import { Drawer } from '../components/ui/Drawer';

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low' | 'warn' | 'info';

interface SeverityTone { fg: string; bg: string; border: string; rail: string }
const SEVERITY_TONES: Record<string, SeverityTone> = {
  critical: { fg: 'var(--danger)',    bg: 'var(--danger-soft)',   border: 'rgba(239, 68, 68, 0.3)', rail: '#ef4444' },
  high:     { fg: 'var(--danger)',    bg: 'var(--danger-soft)',   border: 'rgba(239, 68, 68, 0.3)', rail: '#f87171' },
  medium:   { fg: 'var(--warning)',   bg: 'var(--warning-soft)',  border: 'rgba(245, 158, 11, 0.3)', rail: '#f59e0b' },
  low:      { fg: 'var(--warning)',   bg: 'var(--warning-soft)',  border: 'rgba(245, 158, 11, 0.3)', rail: '#fbbf24' },
  // The license server stores info | warn | critical (anomaly_events.severity): `warn` is a warning, not an unknown value.
  warn:     { fg: 'var(--warning)',   bg: 'var(--warning-soft)',  border: 'rgba(245, 158, 11, 0.3)', rail: '#f59e0b' },
  info:     { fg: 'var(--text-muted)', bg: 'var(--bg-overlay)',   border: 'var(--border)',           rail: '#64748b' },
};
const FALLBACK_TONE: SeverityTone = SEVERITY_TONES.info!;

function bandForSeverity(severity: string): Band {
  switch (severity.toLowerCase()) {
    case 'critical': return 'critical';
    case 'high':     return 'critical';
    case 'medium':   return 'warning';
    case 'low':      return 'degraded';
    case 'warn':     return 'warning';
    case 'info':     return 'neutral';
    default:         return 'neutral';
  }
}

function toneFor(severity: string): SeverityTone {
  return SEVERITY_TONES[severity.toLowerCase()] ?? FALLBACK_TONE;
}

export function AnomaliesPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<AnomalyRow[] | null>(null);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const perms = usePermissions();
  const canDelete = perms.canOwner;

  useEffect(() => {
    setRefreshing(true);
    new AdminApiClient({ baseUrl: '', getToken: () => state.token })
      .anomalies(unresolvedOnly)
      .then((r) => { setRows(r.anomalies); setErr(null); })
      .catch((e) => { setRows(null); setErr(failureFrom(e, 'anomalies')); })
      .finally(() => setRefreshing(false));
  }, [state.token, unresolvedOnly, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm('Dismiss this anomaly? Audit log will keep a full-row snapshot.')) return;
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    try {
      await api.deleteAnomaly(id);
      setOpenId(null);
      onRefresh();
    } catch (e) {
      alert(`Could not dismiss the anomaly. ${userMessageFor(e, 'this anomaly')}`);
    }
  };

  // The KPIs describe ACTIVE alarms (the hero says so), so a resolved one is not counted; the filter chips below count every row.
  const counts = useMemo(() => {
    const c = { active: 0, critical: 0, high: 0, medium: 0, low: 0, warn: 0, info: 0 };
    for (const r of rows ?? []) {
      if (r.resolved_at) continue;
      c.active += 1;
      const s = r.severity.toLowerCase();
      if (s === 'critical' || s === 'high' || s === 'medium' || s === 'low' || s === 'warn' || s === 'info') c[s] += 1;
    }
    return c;
  }, [rows]);
  const totals = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0, warn: 0, info: 0 };
    for (const r of rows ?? []) {
      const s = r.severity.toLowerCase();
      if (s === 'critical' || s === 'high' || s === 'medium' || s === 'low' || s === 'warn' || s === 'info') c[s] += 1;
    }
    return c;
  }, [rows]);
  // "Active" is as serious as the worst active alarm: informational ones alone are not an emergency.
  const activeBand: Band = counts.active === 0 ? 'healthy'
    : counts.critical + counts.high > 0 ? 'critical'
    : counts.medium + counts.warn > 0 ? 'warning'
    : counts.low > 0 ? 'degraded' : 'neutral';

  const filtered = useMemo(() => {
    return (rows ?? []).filter((r) => {
      if (severityFilter !== 'all' && r.severity.toLowerCase() !== severityFilter) return false;
      return true;
    });
  }, [rows, severityFilter]);

  const focused = useMemo(
    () => (rows ?? []).find((r) => r.id === openId) ?? null,
    [rows, openId],
  );

  const severityChips: Array<{ key: SeverityFilter; label: string; count: number }> = [
    { key: 'all',      label: 'All',      count: rows?.length ?? 0 },
    { key: 'critical', label: 'Critical', count: totals.critical },
    { key: 'high',     label: 'High',     count: totals.high },
    { key: 'medium',   label: 'Medium',   count: totals.medium },
    { key: 'low',      label: 'Low',      count: totals.low },
    { key: 'warn',     label: 'Warning',  count: totals.warn },
    { key: 'info',     label: 'Info',     count: totals.info },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Anomalies"
        subtitle="Heuristic alarms — clock skew, abnormal heartbeat patterns, suspicious activations."
        icon={AlertTriangle}
        accent="#fbbf24"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      {rows && (
      <HeroPanel
        icon={AlertTriangle}
        accent="#fbbf24"
        title="Anomaly load"
        subtitle="Active alarms, broken down by severity."
        kpis={[
          <HeroKpi key="a" label="Active" value={counts.active}
                   band={activeBand} />,
          <HeroKpi key="c" label="Critical" value={counts.critical}
                   band={counts.critical > 0 ? 'critical' : 'neutral'} />,
          <HeroKpi key="h" label="High" value={counts.high}
                   band={counts.high > 0 ? 'critical' : 'neutral'} />,
          <HeroKpi key="m" label="Medium" value={counts.medium}
                   band={counts.medium > 0 ? 'warning' : 'neutral'} />,
          <HeroKpi key="l" label="Low" value={counts.low}
                   band={counts.low > 0 ? 'degraded' : 'neutral'} />,
          <HeroKpi key="w" label="Warning" value={counts.warn}
                   band={counts.warn > 0 ? 'warning' : 'neutral'} />,
          <HeroKpi key="i" label="Info" value={counts.info}
                   band="neutral" />,
        ]}
      />
      )}

      <div style={{ marginTop: 16 }}>
        <AutoResolveRules />
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <ChipRow chips={severityChips} value={severityFilter} onChange={setSeverityFilter} />
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg-overlay)',
              fontSize: '0.75rem', color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={unresolvedOnly}
              onChange={(e) => setUnresolvedOnly(e.target.checked)}
            />
            Unresolved only
          </label>
        </div>

        <FetchState
          loading={!rows && !err}
          error={err}
          empty={!!rows && filtered.length === 0}
          emptyTitle="No anomalies match"
          emptyDescription="Adjust filters or refresh — anomalies show up here as the engine surfaces issues."
          onRetry={onRefresh}
        >
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((r) => (
              <AnomalyCard key={r.id} row={r} onClick={() => setOpenId(r.id)} />
            ))}
          </ul>
        </FetchState>
      </div>

      <AnomalyDrawer
        row={focused}
        canDelete={canDelete}
        ownerReason={perms.ownerReason}
        onClose={() => setOpenId(null)}
        onDelete={focused ? () => void onDelete(focused.id) : undefined}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Auto-resolve rules card
 *
 * Currently illustrative — rules are baked into the engine; if/when
 * /internal/anomaly-rules lands we'll fetch them. The names match
 * what's currently hard-coded in superVision.AnomalyDetector.
 * ────────────────────────────────────────────────────────────────── */

interface AutoResolveRule {
  code: string;
  description: string;
  windowH: number;
  enabled: boolean;
}

const AUTO_RESOLVE_RULES: AutoResolveRule[] = [
  { code: 'clock_skew_minor',     description: 'Clock skew under 5 minutes — auto-resolves once heartbeat aligns.', windowH: 1,  enabled: true },
  { code: 'heartbeat_gap_short',  description: 'Single missed heartbeat — auto-resolves on the next successful tick.', windowH: 1,  enabled: true },
  { code: 'activation_burst',     description: 'Activation count jumped >50% in 1h — auto-resolves once new heartbeats stabilize.', windowH: 6,  enabled: true },
  { code: 'sso_stale_token',      description: 'Stale SSO token replay — auto-resolves on refresh.', windowH: 24, enabled: true },
  { code: 'cert_rotation_gap',    description: 'Cert rotation gap during the 6h refresh window — auto-resolves at next refresh.', windowH: 12, enabled: false },
];

function AutoResolveRules(): JSX.Element {
  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Settings2 width={14} height={14} color="var(--text-muted)" />
        <h3
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Auto-resolve rules
        </h3>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
          }}
        >
          reference only — not read from the server; the engine decides
        </span>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {AUTO_RESOLVE_RULES.map((rule) => (
          <li
            key={rule.code}
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--bg-overlay)',
              border: '1px solid var(--border)',
              opacity: rule.enabled ? 1 : 0.55,
            }}
          >
            <span
              aria-hidden
              style={{
                flex: 'none',
                width: 28, height: 28,
                borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: rule.enabled ? 'rgba(52, 211, 153, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                color: rule.enabled ? 'var(--success)' : 'var(--text-muted)',
                border: rule.enabled ? '1px solid rgba(52, 211, 153, 0.4)' : '1px solid var(--border)',
              }}
            >
              {rule.enabled ? <ShieldCheck width={13} height={13} /> : <ShieldOff width={13} height={13} />}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <code
                  style={{
                    fontSize: '0.75rem', fontWeight: 600,
                    color: 'var(--text-primary)', fontFamily: 'inherit',
                  }}
                >
                  {rule.code}
                </code>
                <StatusChip band={rule.enabled ? 'healthy' : 'neutral'} size="sm" noDot>
                  {rule.enabled ? 'on' : 'off'}
                </StatusChip>
                <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                  window {rule.windowH}h
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {rule.description}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Severity-railed anomaly card
 * ────────────────────────────────────────────────────────────────── */

function AnomalyCard({
  row, onClick,
}: { row: AnomalyRow; onClick: () => void }): JSX.Element {
  const tone = toneFor(row.severity);
  return (
    <li>
      <button
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'stretch', gap: 0,
          width: '100%', padding: 0,
          borderRadius: 12,
          border: `1px solid ${tone.border}`,
          background: 'var(--bg-raised)',
          textAlign: 'left',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'border-color 120ms ease, transform 120ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = tone.rail;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = tone.border;
        }}
      >
        <span
          aria-hidden
          style={{
            flex: 'none',
            width: 4,
            background: `linear-gradient(180deg, ${tone.rail}, ${tone.rail}88)`,
          }}
        />
        <div style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <StatusChip band={bandForSeverity(row.severity)} pulse={!row.resolved_at}>
              {row.severity}
            </StatusChip>
            <code
              style={{
                fontSize: '0.6875rem',
                background: 'var(--bg-overlay)',
                padding: '2px 6px',
                borderRadius: 4,
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
              }}
            >
              {row.code}
            </code>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
              <Clock width={11} height={11} />
              {fmtRelative(row.detected_at)}
            </span>
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.4 }}>
            {row.message}
          </div>
          {(row.license_id || row.action_required || row.resolved_at) && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
              {row.license_id && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                  <KeyRound width={11} height={11} />
                  <code>{row.license_id.slice(0, 8)}…</code>
                </span>
              )}
              {row.action_required && (
                <span style={{ fontSize: '0.6875rem', color: tone.fg, fontWeight: 500 }}>
                  ⚑ action required
                </span>
              )}
              {row.resolved_at && (
                <StatusChip band="healthy" size="sm" noDot>
                  resolved {fmtRelative(row.resolved_at)}
                </StatusChip>
              )}
            </div>
          )}
        </div>
      </button>
    </li>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Drawer with notes + dismiss
 * ────────────────────────────────────────────────────────────────── */

function AnomalyDrawer({
  row, canDelete, ownerReason, onClose, onDelete,
}: {
  row: AnomalyRow | null;
  canDelete: boolean;
  ownerReason: string | null;
  onClose: () => void;
  onDelete?: () => void;
}): JSX.Element {
  const tone = row ? toneFor(row.severity) : null;

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width="md"
      title={
        row ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle width={14} height={14} color={tone?.rail} />
            <code>{row.code}</code>
          </span>
        ) : ''
      }
      subtitle={row ? `Detected ${fmtRelative(row.detected_at)} · ${row.severity}` : undefined}
      footer={
        row && (
          <>
            {canDelete && onDelete && (
              <Button variant="default" size="sm" onClick={onDelete}>
                <Trash2 width={14} height={14} />
                Dismiss
              </Button>
            )}
          </>
        )
      }
    >
      {row && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Anomaly">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Severity">
                <StatusChip band={bandForSeverity(row.severity)} pulse={!row.resolved_at}>
                  {row.severity}
                </StatusChip>
              </KV>
              <KV label="Code"><code>{row.code}</code></KV>
              <KV label="Detected">{row.detected_at.slice(0, 16).replace('T', ' ')}Z</KV>
              <KV label="State">
                {row.resolved_at
                  ? <StatusChip band="healthy" size="sm" noDot>resolved</StatusChip>
                  : <StatusChip band="critical" size="sm" pulse>unresolved</StatusChip>}
              </KV>
              <KV label="Action required">
                {row.action_required ? (
                  <span style={{ color: tone?.fg }}>yes</span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>no</span>
                )}
              </KV>
              {row.license_id && (
                <KV label="License">
                  <code style={{ fontSize: '0.75rem' }}>{row.license_id}</code>
                </KV>
              )}
            </div>
          </Section>

          <Section title="Message">
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                padding: 12,
                borderRadius: 8,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                lineHeight: 1.5,
              }}
            >
              {row.message}
            </p>
          </Section>

          <Section title="Acknowledge">
            <p
              style={{
                margin: 0,
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              <ShieldCheck
                width={11} height={11}
                style={{ verticalAlign: 'middle', marginRight: 4 }}
              />
              {canDelete
                ? 'Use “Dismiss” to remove the anomaly from the active queue. The pre-delete row is preserved in the audit log.'
                : `Dismissing an anomaly is not available to you. ${ownerReason ?? ''}`}
            </p>
          </Section>
        </div>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        flexWrap: 'wrap',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', gap: 5, alignItems: 'center',
              padding: '5px 9px', border: 'none', borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function Section({
  title, hint, children,
}: { title: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        {title}
      </h3>
      {children}
      {hint && (
        <p style={{ margin: '6px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {hint}
        </p>
      )}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ fontSize: '0.8125rem' }}>{children}</div>
    </div>
  );
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
